import Close from '@mui/icons-material/Close';
import EditOutlined from '@mui/icons-material/EditOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useQueryClient } from '@tanstack/react-query';
import {
  patchV1CompaniesCompanyIdLoadsLoadIdPricing,
  Pricing_Override_Update,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { ReactNode, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';

import { Chip } from '~components/Chip/Chip';
import NumberInput from '~components/FormFields/v2/NumberInput';
import { formatLoadRate } from '~components/Job/LoadCustomRateIndicator';
import {
  buildRateRows,
  RATE_VALUE_FIELD_BY_SIDE,
  RateSide,
  RESOLUTION_CHIP_COLOR,
  resolutionLabel,
} from '~components/Job/LoadPricing/loadPricingHelpers';
import PricingEventsTimeline from '~components/Job/LoadPricing/PricingEventsTimeline';
import {
  LoadingSpinner,
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useInvalidateQueriesOnJobUpdate from '~hooks/useInvalidateQueriesOnJobUpdate';
import { JobRateEditable } from '~hooks/useJob/models';
import useJobLoadsPricingQuery, {
  jobLoadsPricingQueryKey,
} from '~queries/useJobLoadsPricingQuery';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// Dropdown sentinel for the Job-grain (all-loads) pricing timeline — the default view, with
// individual Loads listed underneath for drill-down.
const JOB_VIEW = '__job__';

const SectionTitle = ({ children }: { children: ReactNode }) => {
  const theme = useTheme();

  return (
    <Typography
      sx={{ color: theme.brandV2.colors.treadBlack, fontSize: '13px', fontWeight: 600 }}
    >
      {children}
    </Typography>
  );
};

interface LoadPricingPanelProps {
  jobId: string;
  rateEditable: JobRateEditable;
}

/**
 * "Pricing" tab content for the Load details panel (`load_level_pricing` POC).
 * Shows, for one selected Load of the job: a compact customer/vendor/driver
 * rates summary with resolution badges, the inherited vs load-specific status,
 * an optional manual override mini-form (PATCH /v1/loads/:id/pricing), and the
 * Load's pricing-event history timeline.
 */
export default function LoadPricingPanel({ jobId, rateEditable }: LoadPricingPanelProps) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const { toasterStore, userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();

  const [selectedKey, setSelectedKey] = useState<string>(JOB_VIEW);
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const { control, handleSubmit, reset } = useForm<Partial<Record<RateSide, string>>>({
    defaultValues: {},
  });

  const { data: loads, isLoading, isError } = useJobLoadsPricingQuery(jobId);

  // The cascade/divergence that re-rates these Loads runs async in the worker, so the own-save
  // invalidation below can beat it. Refetch the rate summary AND the event timeline when the
  // backend pushes an update for this job over CompanyJobUpdateChannel (fired after the worker
  // finishes) — the race-free freshness signal.
  useInvalidateQueriesOnJobUpdate(
    companyId,
    [jobId],
    [jobLoadsPricingQueryKey(jobId), ['pricingEvents']],
  );

  const isJobView = selectedKey === JOB_VIEW;

  // The Load whose rates the summary shows: the picked one, or the first as a representative for
  // the Job-grain view (Loads inherit the same rates unless one has diverged).
  const selectedLoad = useMemo(() => {
    if (!loads?.length) {
      return undefined;
    }

    return loads.find((load) => load.id === selectedKey) ?? loads[0];
  }, [loads, selectedKey]);

  const rateRows = useMemo(
    () => (selectedLoad ? buildRateRows(selectedLoad) : []),
    [selectedLoad],
  );

  const hasEditableRateRow = rateRows.some((row) => rateEditable[row.side]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
        <LoadingSpinner isVisible loadingIndicatorSize={16} />
      </Box>
    );
  }

  // Distinguish a failed fetch from a genuinely empty job: getJobLoads surfaces the error (the
  // hook toasts and re-throws), so React Query lands in the error state here. Without this branch
  // the panel would fall through to "no loads" and misrepresent a transient failure as empty.
  if (isError) {
    return (
      <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3 }}>
        {t('loads.pricing.failed_to_load')}
      </Typography>
    );
  }

  if (!loads?.length || !selectedLoad) {
    return (
      <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3 }}>
        {t('loads.pricing.no_loads')}
      </Typography>
    );
  }

  const startEditing = () => {
    reset(Object.fromEntries(rateRows.map((row) => [row.side, String(row.value)])));
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    reset({});
  };

  const saveOverrides = handleSubmit(async (values) => {
    const body: Pricing_Override_Update = {};

    rateRows.forEach((row) => {
      if (!rateEditable[row.side]) return;

      const draft = values[row.side]?.trim();

      if (draft && Number(draft) !== row.value && !Number.isNaN(Number(draft))) {
        body[RATE_VALUE_FIELD_BY_SIDE[row.side]] = Number(draft);
      }
    });

    if (!Object.keys(body).length) {
      stopEditing();
      return;
    }

    try {
      setIsSaving(true);
      await patchV1CompaniesCompanyIdLoadsLoadIdPricing({
        path: {
          'company-id': companyId,
          'load-id': selectedLoad.id,
        },
        body,
      });

      toasterStore.push(alert(t('loads.pricing.rates_updated'), AlertTypes.success));
      stopEditing();

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: jobLoadsPricingQueryKey(jobId) }),
        queryClient.invalidateQueries({ queryKey: ['pricingEvents'] }),
      ]);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.load.failed_to_update_pricing'),
      );
    } finally {
      setIsSaving(false);
    }
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <TextField
        label={t('loads.pricing.select_view')}
        onChange={(event) => {
          stopEditing();
          setSelectedKey(event.target.value);
        }}
        select
        size="small"
        sx={{ maxWidth: '220px' }}
        value={selectedKey}
      >
        <MenuItem value={JOB_VIEW}>{t('loads.pricing.job_view')}</MenuItem>
        {loads.map((load, index) => (
          <MenuItem key={load.id} value={load.id}>
            {`${t('status.load')} ${index + 1} (${load.loadId})`}
          </MenuItem>
        ))}
      </TextField>

      <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
        <SectionTitle>{t('loads.pricing.rates')}</SectionTitle>

        {selectedLoad.rateInherited !== null && (
          <Chip
            color={selectedLoad.rateInherited ? 'default' : 'warning'}
            label={
              selectedLoad.rateInherited
                ? t('loads.pricing.inherited_from_job_assignment')
                : t('loads.pricing.load_specific_pricing')
            }
            hideDropdownArrow
          />
        )}

        {hasEditableRateRow && !isEditing && !isJobView && (
          <IconButton
            aria-label={t('loads.pricing.edit_rates')}
            onClick={startEditing}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <EditOutlined sx={{ fontSize: '16px' }} />
          </IconButton>
        )}

        {isEditing && (
          <IconButton
            aria-label={t('actions.cancel')}
            disabled={isSaving}
            onClick={stopEditing}
            size="small"
            sx={{ ml: 'auto' }}
          >
            <Close sx={{ fontSize: '16px' }} />
          </IconButton>
        )}
      </Box>

      {rateRows.length ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {rateRows.map((row) => (
            <Box
              key={row.side}
              sx={{ alignItems: 'center', display: 'flex', gap: 1, minHeight: '28px' }}
            >
              <Typography variant="body2" sx={{ minWidth: '76px', fontWeight: 600 }}>
                {row.label}
              </Typography>

              {isEditing && rateEditable[row.side] ? (
                <NumberInput
                  control={control}
                  name={row.side}
                  slotProps={{ htmlInput: { step: '0.01', min: '0' } }}
                  sx={{ maxWidth: '120px' }}
                />
              ) : (
                <Typography variant="body2">
                  {formatLoadRate(currencyFormatter, row.value, row.type)}
                </Typography>
              )}

              {row.resolution && (
                <Box sx={{ ...(row.resolution === 'frozen' ? { opacity: 0.55 } : {}) }}>
                  <Chip
                    color={RESOLUTION_CHIP_COLOR[row.resolution] ?? 'default'}
                    label={resolutionLabel(row.resolution)}
                    hideDropdownArrow
                  />
                </Box>
              )}
            </Box>
          ))}

          {isEditing && (
            <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
              <SmallLoadingButton
                color="brandV2Yellow"
                loading={isSaving}
                onClick={saveOverrides}
              >
                {t('actions.save')}
              </SmallLoadingButton>

              <SmallButton color="secondary" disabled={isSaving} onClick={stopEditing}>
                {t('actions.cancel')}
              </SmallButton>
            </Box>
          )}
        </Box>
      ) : (
        <Typography variant="body2" sx={{ color: theme.brandV2.colors.treadGray3 }}>
          {t('loads.pricing.no_pricing_data')}
        </Typography>
      )}

      <SectionTitle>{t('loads.pricing.pricing_history')}</SectionTitle>

      {isJobView ? (
        <PricingEventsTimeline jobId={jobId} />
      ) : (
        <PricingEventsTimeline subjectId={selectedLoad.id} subjectType="Load" />
      )}
    </Box>
  );
}
